import  { Fragment, useState, useEffect, useContext } from "react";
import {  useLocation, Outlet, useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Layout,
  Menu,
  Typography,
  Space,
  Button,
  Avatar,
  Dropdown,
  Badge,
  message,
  Drawer,
  Grid,
  Spin,
  Empty,
  Modal,
  Alert,
  Tooltip,
} from "antd";
import {
  DashboardOutlined,
  TeamOutlined,
  FileTextOutlined,
  MessageOutlined,
  BarChartOutlined,
  SettingOutlined,
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  WhatsAppOutlined,
  MenuOutlined,
  InboxOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
    CloseOutlined,
  RocketOutlined,
  QuestionCircleOutlined,
  MailOutlined,
  StopOutlined,
  ReadOutlined,
  SwapOutlined,
  KeyOutlined,
  PlusOutlined,
  WalletOutlined,
  SendOutlined,
  ApiOutlined,
  UsergroupAddOutlined,
  RobotOutlined,
  ApartmentOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/authContext";
import { useCompany } from "../context/companyContext";
import { useWebSocketClient } from "../context/websocketContext";
import { useSidebar } from "../context/sidebarContext";
import { useWaba } from "../context/wabaContext";
import { useChannelMode } from "../context/channelModeContext";
import { useUserContext, hasPageAccess, canWrite } from "../context/userContext";
import { useOnboarding } from "../context/onboardingContext";
import { useProfile } from "../context/profileContext";
import axiosConfig from "../config/axiosConfig";
import Step2Gif from "../assets/gif/step_2_Connect.gif";
import Step3Gif from "../assets/gif/step_3_Upload.gif";
import Step4Gif from "../assets/gif/step_4_send.gif";
import ConnectStepImage from "../assets/images/guide-step2-connect.png";
import ContactsStepImage from "../assets/images/guide-step3-contacts.png";
import SendStepImage from "../assets/images/guide-step5-send.png";
import PricingStepImage from "../assets/images/pricing-step-illustration.png";
import MessageOSLogo from "../assets/images/final-horizontal-white.png";
import StepsContainer from "../components/StepsContainer";
import "./DashboardTheme.css";
import "./SidebarNav.css";

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export default function DashboardLayout() {
  const { collapsed, setCollapsed, mobileDrawerVisible, setMobileDrawerVisible } = useSidebar();
  // Sidebar is now a simple drawer menu (no hover-expand)
  const { mode: channelMode, toggleMode: toggleChannelMode } = useChannelMode();
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationLoading, setNotificationLoading] = useState(false);
  
  // Unread message count for Inbox
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [notificationSoundEnabled, setNotificationSoundEnabled] = useState(true);
  const [lastProcessedMessageId, setLastProcessedMessageId] = useState(null);
  
  // Tutorial/Steps modal state
  const [tutorialModalVisible, setTutorialModalVisible] = useState(false);
  
  // Email setup guide modal state
  const [emailSetupModalVisible, setEmailSetupModalVisible] = useState(false);
  
  // Plan & WABA gate state (modals removed — using in-page blur + direct navigation)
  
  // WABA Details from Context
  const { wabaDetails, loadingWabaDetails, wabaFetchError, getWabaStatusDisplay } = useWaba();

  // Onboarding / plan status
  const { onboardingStatus } = useOnboarding();
  const hasActivePlan = onboardingStatus ? onboardingStatus.has_active_plan === true : true; // fail-open
  const wabaSetupComplete = onboardingStatus ? onboardingStatus.waba_setup_complete === true : true; // fail-open
  const isEnterprisePlan = (onboardingStatus?.plan_name || '').toLowerCase() === 'enterprise';

  // Partner toast dismissed state (session-based - reappears on new login)
  const [partnerToastDismissed, setPartnerToastDismissed] = useState(() => {
    return sessionStorage.getItem('partnerToastDismissed') === 'true';
  });
  
  // Dismiss partner toast handler
  const dismissPartnerToast = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setPartnerToastDismissed(true);
    sessionStorage.setItem('partnerToastDismissed', 'true');
  };
  
  const { currentUser, logout } = useContext(AuthContext);
  const { companyData, companyLogoSrc, loadCompanyLogo } = useCompany();
  const { profile } = useProfile();
  const { userCtx } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // ── Route-level auto-gating ──
  // Paths that are ALWAYS accessible (no plan required)
  const planAllowedPaths = ['/dashboard/admin', '/dashboard/pricing', '/dashboard/guide', '/dashboard/profile'];
  // WhatsApp-specific paths that require WABA embedded signup
  const wabaGatedPaths = ['/dashboard/templates', '/dashboard/broadcast', '/dashboard/inbox', '/dashboard/flow-responses', '/dashboard/opted-out', '/dashboard/analytics', '/dashboard/new-template-broadcast'];

  // Determine if the current route is plan-gated or WABA-gated
  const currentPath = location.pathname;
  const isOnPlanAllowedPage = planAllowedPaths.some(p => currentPath.startsWith(p)) || currentPath === '/dashboard' || currentPath === '/dashboard/customer';
  const isOnWabaGatedPage = wabaGatedPaths.some(p => currentPath.startsWith(p));

  // Email paths are always accessible after plan purchase (no WABA needed)
  const isOnEmailPage = currentPath.startsWith('/dashboard/email');
  // Contacts page is shared between email & whatsapp — always accessible after plan
  const isOnContactsPage = currentPath.startsWith('/dashboard/contacts');

  // No auto-modal: we use in-page blur overlays with direct navigation instead
  
  // Get WebSocket messages to track incoming messages
  let wsMessages = [];
  try {
    const wsContext = useWebSocketClient();
    wsMessages = wsContext?.messages || [];
  } catch (e) {
    // WebSocket might not be available in all contexts
  }
  
  // Prefetch: Check if user already has a partner request before navigating to the page
  const prefetchPartnerRequestAndNavigate = async () => {
    try {
      const customerUuid = companyData?.customeruuid;
      if (!customerUuid) {
        // No UUID available; navigate normally
        navigate("/dashboard/become-solution-partner");
        return;
      }

      const hide = message.loading({ content: 'Checking partner request status...', key: 'partner-status', duration: 0 });
      const url = `/dashboard/partner-request?customeruuid=${customerUuid}`;
      const response = await axiosConfig.get(url);
      hide();

      if (response.data?.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const application = response.data.data[0];
        // If pending(0) or approved(1), pass it to the page so it renders status immediately
        if (application.status === 0 || application.status === 1) {
          navigate("/dashboard/become-solution-partner", { state: { prefetchApplication: application } });
          return;
        }
      }
      // Default: navigate to form (rejected or none)
      navigate("/dashboard/become-solution-partner");
    } catch (e) {
      message.destroy('partner-status');
      // On error, still navigate; the page will handle errors gracefully
      navigate("/dashboard/become-solution-partner");
    }
  };
  
  // Get company type and admin level from sessionStorage
  const [companyType, setCompanyType] = useState(null);
  const [adminLevel, setAdminLevel] = useState(null);
  const [partnerApplicationStatus, setPartnerApplicationStatus] = useState(null);
  const [checkingPartnerStatus, setCheckingPartnerStatus] = useState(false);
  
  useEffect(() => {
    // Get company type from sessionStorage or currentUser
    const storedCompanyType = sessionStorage.getItem('company_type') || currentUser?.company_type;
    const storedAdminLevel = sessionStorage.getItem('admin_level') || userCtx?.admin_level || null;
    console.log('Company Type:', storedCompanyType, '| Admin Level:', storedAdminLevel); // Debug log
    setCompanyType(storedCompanyType);
    setAdminLevel(storedAdminLevel);
    
    // Check partner application status if user is a direct customer
    if (storedCompanyType === 'DIRECT_CUSTOMER' && currentUser?.customeruuid) {
      checkPartnerApplicationStatus(currentUser.customeruuid);
    }
  }, [currentUser, userCtx?.admin_level]);

  // Load company logo from authenticated backend (mirrors InboxPage signed URL pattern)
  useEffect(() => {
    if (currentUser) {
      loadCompanyLogo();
    }
  }, [currentUser, loadCompanyLogo]);

  // Listen for 403 permission-denied events from axios interceptor
  useEffect(() => {
    const handlePermissionDenied = (e) => {
      message.error(e.detail?.message || "You don't have permission for this action");
    };
    window.addEventListener('permission-denied', handlePermissionDenied);
    return () => window.removeEventListener('permission-denied', handlePermissionDenied);
  }, []);

  // Listen for incoming messages via WebSocket and update unread count
  useEffect(() => {
    if (wsMessages && wsMessages.length > 0) {
      const latestMessage = wsMessages[wsMessages.length - 1];
      const messageId = latestMessage?.id || latestMessage?.messageId;
      
      // Only increment count if this is a new message (not already processed)
      if (messageId && messageId !== lastProcessedMessageId) {
        // If user is NOT on inbox, increment count and play sound
        if (location.pathname !== '/dashboard/inbox') {
          setUnreadMessageCount(prev => prev + 1);
          if (notificationSoundEnabled) {
            playNotificationSound();
          }
        }
        // Mark message as processed regardless of location
        setLastProcessedMessageId(messageId);
      }
    }
  }, [wsMessages, location.pathname, notificationSoundEnabled, lastProcessedMessageId]);

  // Clear unread count when user manually marks messages as seen
  const clearUnreadCount = () => {
    setUnreadMessageCount(0);
    setLastProcessedMessageId(null);
  };
  
  // Listen for unread count sync events from InboxPage
  useEffect(() => {
    const handleUnreadCountSync = (event) => {
      const { count } = event.detail;
      console.log('📥 Received unread count sync from InboxPage:', count);
      setUnreadMessageCount(count);
    };
    
    window.addEventListener('inboxUnreadCountSync', handleUnreadCountSync);
    return () => {
      window.removeEventListener('inboxUnreadCountSync', handleUnreadCountSync);
    };
  }, []);

  // Play notification sound (WhatsApp style — soft two-note chime)
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const t = audioContext.currentTime;

      const playNote = (freq, startOffset, duration, peakGain) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(audioContext.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, t + startOffset);
        gain.gain.setValueAtTime(0, t + startOffset);
        gain.gain.linearRampToValueAtTime(peakGain, t + startOffset + 0.02); // quick attack
        gain.gain.exponentialRampToValueAtTime(0.001, t + startOffset + duration); // soft decay
        osc.start(t + startOffset);
        osc.stop(t + startOffset + duration);
      };

      // Real WhatsApp chime: low note (D5 ~587Hz) then higher note (A5 ~880Hz)
      playNote(587.33, 0,    0.18, 0.12); // first note — soft
      playNote(880.00, 0.10, 0.25, 0.10); // second note — slightly softer
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  };


  // Check partner application status
  const checkPartnerApplicationStatus = async (customerUuid) => {
    try {
      setCheckingPartnerStatus(true);
      const response = await axiosConfig.get(`/dashboard/partner-request?customeruuid=${customerUuid}`);
      
      if (response.data?.status === true && response.data?.entity?.requests) {
        const applications = response.data.entity.requests;
        if (applications.length > 0) {
          const latestApplication = applications[0];
          // Convert status string to normalized value
          let status = 'none';
          if (latestApplication.status?.toLowerCase() === 'pending') {
            status = 'pending';
          } else if (latestApplication.status?.toLowerCase() === 'approved') {
            status = 'approved';
          } else if (latestApplication.status?.toLowerCase() === 'rejected') {
            status = 'rejected';
          }
          setPartnerApplicationStatus(status);
        } else {
          setPartnerApplicationStatus('none');
        }
      } else {
        setPartnerApplicationStatus('none');
      }
    } catch (error) {
      console.error('Error checking partner application status:', error);
      setPartnerApplicationStatus('none');
    } finally {
      setCheckingPartnerStatus(false);
    }
  };

  // Fetch notifications on mount
  // useEffect(() => {
  //   // Fetch notifications immediately on mount
  //   fetchNotifications();

  //   // Set up interval to fetch notifications every 1 minute (60000 ms)
  //   const notificationInterval = setInterval(() => {           
  //     fetchNotifications();
  //   }, 60000); // 60000 ms = 1 minute

  //   // Cleanup interval on component unmount
  //   return () => {
  //     clearInterval(notificationInterval);
  //   };
  // }, []);

  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      const response = await axiosConfig.post("/getnotification", {}); // API endpoint for notifications
      if (response.data?.statuscode === 200) {
        const count = response.data.entity.unseencount;
        const notifications = response.data.entity.notifications;
        setNotifications(notifications || []);
        setNotificationCount(count || 0);
      } else {
        // Don't show error message for background calls
        console.error("Failed to fetch notifications");
      }
    } catch (error) {
      console.error("Failed to fetch notifications", error);
      // Only show error message on initial load, not for background calls
      if (notifications.length === 0) {
        // message.error("Failed to fetch notifications");
      }
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleNotificationClick = async (notificationids,status) => {
    if (status==='seen') return;
    
    try {
      // Extract notification IDs from the response/state

      const response = await axiosConfig.post("/notification/seen", {
        notificationids: notificationids,
      });

      if (response.data?.statuscode === 200) {
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error marking notifications as seen:", error);
      message.error("Failed to mark notifications as seen");
    }
  };
  const hanldleMarkAllSeen = () => {
    const unseenNotificationIds = notifications
      .filter((notification) => notification.status === "unseen")
      .map((notification) => notification.notificationid);
    if (!unseenNotificationIds.length) return;

    handleNotificationClick(unseenNotificationIds);
  };

  // Get current active menu based on URL
  const getCurrentMenuKey = () => {
    const path = location.pathname;

    // ── Email-mode sub-paths map to the same sidebar keys ──
    if (path === "/dashboard/email" || path === "/dashboard/email/") return "dashboard";
    if (path.startsWith("/dashboard/email/templates")) return "templates";
    if (path.startsWith("/dashboard/email/campaigns")) return "broadcast";
    if (path.startsWith("/dashboard/email/preferences")) return "email-preferences";
    if (path.startsWith("/dashboard/email/settings")) return "dashboard"; // settings sub-nav handles its own highlighting
    if (path.startsWith("/dashboard/email/")) return "dashboard"; // catch-all for other email paths

    if (path === "/dashboard") return "dashboard";
    if (path === "/dashboard/contacts") return "contacts";
    if (path === "/dashboard/templates" || path.startsWith("/dashboard/templates/") || path === "/dashboard/new-template-broadcast") return "templates";
    if (path === "/dashboard/broadcast" || path.startsWith("/dashboard/broadcast/")) return "broadcast";
    if (path === "/dashboard/opted-out" || path.startsWith("/dashboard/opted-out/")) return "opted-out";
    if (path === "/dashboard/inbox") return "inbox";
    if (path === "/dashboard/flow-responses" || path.startsWith("/dashboard/flow-responses/")) return "flow-responses";
    if (path === "/dashboard/pricing") return "pricing";
    if (path === "/dashboard/analytics") return "analytics";
    if (path === "/dashboard/become-solution-partner") return "become-solution-partner";
    if (path === "/dashboard/admin/ai-console") return "admin-ai-console";
    if (path === "/dashboard/admin" || path.startsWith("/dashboard/admin/")) return "admin";
    if (path === "/dashboard/solution-partner-admin" || path.startsWith("/dashboard/solution-partner-admin/")) return "solution-partner-admin";
    if (path === "/dashboard/client-admin" || path.startsWith("/dashboard/client-admin/")) return "client-admin";
    if (path === "/dashboard/ai-chatbot" || path.startsWith("/dashboard/ai-chatbot/")) return "ai-chatbot";
    if (path === "/dashboard/bot-flow" || path.startsWith("/dashboard/bot-flow/")) return "bot-flow";
    if (path === "/dashboard/api-keys" || path.startsWith("/dashboard/api-keys/")) return "api-keys";
    if (path === "/dashboard/transactional-messages" || path.startsWith("/dashboard/transactional-messages/")) return "transactional";
    if (path === "/dashboard/webhooks" || path.startsWith("/dashboard/webhooks/")) return "webhooks";
    if (path === "/dashboard/audience-sources" || path.startsWith("/dashboard/audience-sources/")) return "audience-sources";
    if (path === "/dashboard/external-inbox" || path.startsWith("/dashboard/external-inbox/")) return "inbox";
    if (path === "/dashboard/wallet" || path.startsWith("/dashboard/wallet/")) return "wallet";
    return "dashboard";
  };

  // Resolve the actual navigation path for a menu item (email mode uses /dashboard/email/* paths)
  const getItemPath = (item) => {
    if (item.key === 'ai-chatbot' && userCtx?.pages && !hasPageAccess(userCtx, 'ai_chatbot') && hasPageAccess(userCtx, 'knowledge_base')) {
      return '/dashboard/ai-chatbot/knowledge-base';
    }
    if (channelMode !== 'email') return item.path;
    switch (item.key) {
      case 'dashboard':  return '/dashboard/email';
      case 'templates':  return '/dashboard/email/templates';
      case 'broadcast':  return '/dashboard/email/campaigns';
      case 'contacts':   return '/dashboard/contacts'; // shared — same contacts page
      case 'pricing':    return '/dashboard/pricing';
      case 'analytics':  return '/dashboard/analytics'; // shared for now
      default:           return item.path;
    }
  };

  // Mock data for sidebar stats
  const stats = {
    totalContacts: 1245,
    activeContacts: 987,
    totalTemplates: 12,
    activeTemplates: 8,
    messagesThisMonth: 15420,
    pendingMessages: 127,
    todayCost: 127.5,
    thisMonthCost: 3420.0,
  };

 // Icon wrapper component for sidebar navigation
  const StyledIcon = ({ icon, isCollapsed }) => (
    <div
      className={`dashboard-sidebar-icon-shell${isCollapsed ? " is-collapsed" : ""}`}
      style={{
        width: 22,
        height: 22,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'currentColor',
        fontSize: 18,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
  );

  // All possible menu items with attractive colored icons
  const mosAdminPanelPageKeys = ['admin', 'platform_plans', 'topup_plans', 'payment_management'];
  const hasAnyPageAccess = (pageKeys) => !userCtx?.pages || hasPageAccess(userCtx, pageKeys);

  const allMenuItems = [
    {
      key: "admin",
      pageKey: mosAdminPanelPageKeys,
      iconComponent: (isCollapsed) => <StyledIcon icon={<SafetyCertificateOutlined />} bgColor="#722ed1" isCollapsed={isCollapsed} />,
      themeColor: "#722ed1",
      label: "Admin Dashboard",
      path: "/dashboard/admin",
      badge: null,
      allowedFor: ["ADMIN"], // Only show for ADMIN
    },
    // {
    //   key: "admin-ai-console",
    //   pageKey: "admin",
    //   iconComponent: (isCollapsed) => <StyledIcon icon={<RobotOutlined />} bgColor="#dc2626" isCollapsed={isCollapsed} />,
    //   themeColor: "#dc2626",
    //   label: "AI Super Console",
    //   path: "/dashboard/admin/ai-console",
    //   badge: null,
    //   allowedFor: ["ADMIN"],
    // },
    // ── Solution Partner items commented out — feature deferred ──
    // {
    //   key: "solution-partner-admin",
    //   pageKey: null,
    //   iconComponent: (isCollapsed) => <StyledIcon icon={<TeamOutlined />} bgColor="#13c2c2" isCollapsed={isCollapsed} />,
    //   themeColor: "#13c2c2",
    //   label: "Solution Partner Admin",
    //   path: "/dashboard/solution-partner-admin",
    //   badge: null,
    //   allowedFor: ["SOLUTION_PARTNER"],
    // },
    // {
    //   key: "become-solution-partner",
    //   pageKey: null,
    //   iconComponent: (isCollapsed) => <StyledIcon icon={<StarOutlined />} bgColor="#faad14" isCollapsed={isCollapsed} />,
    //   themeColor: "#faad14",
    //   label: "Become a Solution Partner",
    //   path: "/dashboard/become-solution-partner",
    //   badge: null,
    //   allowedFor: ["DIRECT_CUSTOMER"],
    //   highlighted: true,
    // },
    {
      key: "dashboard",
      pageKey: "dashboard",
      iconComponent: (isCollapsed) => <StyledIcon icon={<DashboardOutlined />} bgColor="#1890ff" isCollapsed={isCollapsed} />,
      themeColor: "#1890ff",
      label: "Dashboard",
      path: "/dashboard",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"], // Show for all
    },
    {
      key: "contacts",
      pageKey: "contacts",
      iconComponent: (isCollapsed) => <StyledIcon icon={<TeamOutlined />} bgColor="#52c41a" isCollapsed={isCollapsed} />,
      themeColor: "#52c41a",
      label: "Contacts",
      path: "/dashboard/contacts",
      badge: stats.totalContacts,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"], // Show for all
    },
    {
      key: "templates",
      pageKey: "templates",
      iconComponent: (isCollapsed) => <StyledIcon icon={<FileTextOutlined />} bgColor="#fa8c16" isCollapsed={isCollapsed} />,
      themeColor: "#fa8c16",
      label: "Templates",
      path: "/dashboard/templates",
      badge: stats.totalTemplates,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"], // Show for all
    },
    {
      key: "broadcast",
      pageKey: "campaigns",
      iconComponent: (isCollapsed) => <StyledIcon icon={<MessageOutlined />} bgColor="#eb2f96" isCollapsed={isCollapsed} />,
      themeColor: "#eb2f96",
      label: "Campaign",
      path: "/dashboard/broadcast",
      badge: stats.pendingMessages > 0 ? stats.pendingMessages : null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"], // Show for all
    },
    {
      key: "opted-out",
      pageKey: "campaigns",
      iconComponent: (isCollapsed) => <StyledIcon icon={<StopOutlined />} bgColor="#ff4d4f" isCollapsed={isCollapsed} />,
      themeColor: "#ff4d4f",
      label: "Opt-Outs",
      path: "/dashboard/opted-out",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "inbox",
      pageKey: "inbox",
      iconComponent: (isCollapsed) => <StyledIcon icon={<InboxOutlined />} bgColor="#25D366" isCollapsed={isCollapsed} />,
      themeColor: "#25D366",
      label: "Inbox",
      path: "/dashboard/inbox",
      badge: unreadMessageCount > 0 ? unreadMessageCount : null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"], // Show for all
    },
    {
      key: "flow-responses",
      pageKey: "inbox",
      iconComponent: (isCollapsed) => <StyledIcon icon={<FormOutlined />} bgColor="#008069" isCollapsed={isCollapsed} />,
      themeColor: "#008069",
      label: "Flow Responses",
      path: "/dashboard/flow-responses",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "analytics",
      pageKey: "dashboard",
      iconComponent: (isCollapsed) => <StyledIcon icon={<BarChartOutlined />} bgColor="#f5222d" isCollapsed={isCollapsed} />,
      themeColor: "#f5222d",
      label: "Analytics",
      path: "/dashboard/analytics",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"], // Show for all
    },
    // {
    //   key: "pricing",
    //   pageKey: null,
    //   iconComponent: (isCollapsed) => <StyledIcon icon={<RocketOutlined />} bgColor="#13c2c2" isCollapsed={isCollapsed} />,
    //   themeColor: "#13c2c2",
    //   label: "Pricing & Plans",
    //   path: "/dashboard/pricing",
    //   badge: null,
    //   allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    // },
    {
      key: "ai-chatbot",
      pageKey: ["ai_chatbot", "knowledge_base"],
      iconComponent: (isCollapsed) => <StyledIcon icon={<RobotOutlined />} bgColor="#4f46e5" isCollapsed={isCollapsed} />,
      themeColor: "#4f46e5",
      label: "AI Chatbot",
      path: "/dashboard/ai-chatbot",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "bot-flow",
      pageKey: "bot_flows",
      iconComponent: (isCollapsed) => <StyledIcon icon={<ApartmentOutlined />} bgColor="#7c3aed" isCollapsed={isCollapsed} />,
      themeColor: "#7c3aed",
      label: "Bot Flow Builder",
      path: "/dashboard/bot-flow",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "api-keys",
      pageKey: "api_keys",
      iconComponent: (isCollapsed) => <StyledIcon icon={<KeyOutlined />} bgColor="#13c2c2" isCollapsed={isCollapsed} />,
      themeColor: "#13c2c2",
      label: "API Keys",
      path: "/dashboard/api-keys",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "transactional",
      pageKey: "api_keys",
      iconComponent: (isCollapsed) => <StyledIcon icon={<SendOutlined />} bgColor="#0ea5e9" isCollapsed={isCollapsed} />,
      themeColor: "#0ea5e9",
      label: "Transactional Messages",
      path: "/dashboard/transactional-messages",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "webhooks",
      pageKey: "api_keys",
      iconComponent: (isCollapsed) => <StyledIcon icon={<ApiOutlined />} bgColor="#7c3aed" isCollapsed={isCollapsed} />,
      themeColor: "#7c3aed",
      label: "Webhooks",
      path: "/dashboard/webhooks",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "audience-sources",
      pageKey: "api_keys",
      iconComponent: (isCollapsed) => <StyledIcon icon={<UsergroupAddOutlined />} bgColor="#f97316" isCollapsed={isCollapsed} />,
      themeColor: "#f97316",
      label: "Audience Sources",
      path: "/dashboard/audience-sources",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "wallet",
      pageKey: "wallet",
      iconComponent: (isCollapsed) => <StyledIcon icon={<WalletOutlined />} bgColor="#0d9488" isCollapsed={isCollapsed} />,
      themeColor: "#0d9488",
      label: "Billing & Plans",
      path: "/dashboard/wallet",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER", "ADMIN"],
    },
    {
      key: "client-admin",
      pageKey: "admin",
      iconComponent: (isCollapsed) => <StyledIcon icon={<SettingOutlined />} bgColor="#531dab" isCollapsed={isCollapsed} />,
      themeColor: "#531dab",
      label: "Admin & Settings",
      path: "/dashboard/client-admin",
      badge: null,
      allowedFor: ["DIRECT_CUSTOMER", "SOLUTION_PARTNER"], // Only for clients, not MOS ADMIN
    },
    
  ];

  // Filter menu items based on company type, admin level, partner status, plan status, and page access
  const menuItems = allMenuItems.filter(item => {
    // Hide WhatsApp-only features when in email mode
    const whatsappOnlyKeys = ['inbox', 'flow-responses', 'opted-out', 'ai-chatbot', 'bot-flow', 'api-keys', 'transactional', 'webhooks', 'audience-sources'];
    if (channelMode === 'email' && whatsappOnlyKeys.includes(item.key)) return false;

    // API Keys — enterprise plan only
    if (item.key === 'api-keys' && !isEnterprisePlan) return false;

    if (!companyType) return true; // Show all items if company type is not loaded yet
    
    // ── Admin-level aware filtering (when admin_level is available) ──
    // MOS admin panel: only for MOS admins (super or sub with admin page access)
    if (item.key === 'admin') {
      if (adminLevel) {
        if (adminLevel === 'mos_super_admin') return true; // full access
        if (adminLevel === 'mos_sub_admin') {
          // Sub-admin must have at least one MOS admin panel page in their pages array
          return hasAnyPageAccess(mosAdminPanelPageKeys);
        }
        return false; // client admins never see MOS admin panel
      }
      // Fallback (no admin_level): only ADMIN company_type, respecting pages
      if (userCtx && userCtx.pages && item.pageKey) return hasAnyPageAccess(item.pageKey);
      return companyType === 'ADMIN';
    }
    
    // Client admin panel: for client admins only (never MOS)
    if (item.key === 'client-admin') {
      if (adminLevel) {
        if (adminLevel === 'mos_super_admin' || adminLevel === 'mos_sub_admin') return false;
        if (adminLevel === 'client_super_admin') return true; // full access
        if (adminLevel === 'client_sub_admin') {
          return item.pageKey ? hasPageAccess(userCtx, item.pageKey) : true;
        }
      }
      // Fallback: show for DIRECT_CUSTOMER / SOLUTION_PARTNER (not ADMIN)
      if (companyType === 'ADMIN') return false;
      if (userCtx && userCtx.pages && item.pageKey) return hasPageAccess(userCtx, item.pageKey);
      return item.allowedFor.includes(companyType);
    }
    
    // Special handling for "Become a Solution Partner" button
    if (item.key === 'become-solution-partner') {
      // Only show for direct customers who haven't applied or have been rejected
      if (companyType !== 'DIRECT_CUSTOMER') return false;
      // Hide if application is pending or approved
      if (partnerApplicationStatus === 'pending' || partnerApplicationStatus === 'approved') {
        return false;
      }
      return true;
    }
    
    // Special handling for "Solution Partner Admin" button
    if (item.key === 'solution-partner-admin') {
      // Show for solution partners OR direct customers with approved partner applications
      if (companyType === 'SOLUTION_PARTNER') return true;
      if (companyType === 'DIRECT_CUSTOMER' && partnerApplicationStatus === 'approved') return true;
      return false;
    }
    
    // If userCtx has a pages array (child admin), filter based on page access
    if (userCtx && userCtx.pages && item.pageKey) {
      // In email mode, also check the email-specific page key
      // e.g. sidebar item with pageKey="templates" should also pass if user has "email_templates"
      if (channelMode === 'email') {
        const emailPageKey = 'email_' + item.pageKey;
        if (hasPageAccess(userCtx, item.pageKey) || hasPageAccess(userCtx, emailPageKey)) return true;
        return false;
      }
      return hasPageAccess(userCtx, item.pageKey);
    }
    
    return item.allowedFor.includes(companyType);
  });
  
  // Debug log to see filtered menu items
  console.log('Filtered Menu Items:', menuItems.map(item => item.key), 'for company type:', companyType);

  // Handle user menu actions
  const handleMenuClick = ({ key }) => {
    if (key === "profile") {
      navigate("/dashboard/profile");
    } else if (key === "settings") {
      navigate("/whatsappregistration");
    } else if (key === "api-docs") {
      navigate("/dashboard/api-docs");
    // } else if (key === "become-partner") {
    //   prefetchPartnerRequestAndNavigate();
    } else if (key === "logout") {
      axiosConfig
        .post("/company/logout", { companyid: currentUser?.companyid })
        .then((response) => {
          if (response.data?.statuscode === 200) {
            // message.success(
            //   response.data?.message || "Logged out successfully"
            // );
            logout();
            navigate("/login");
          } else {
            message.error(
              response.data?.message || "Logout failed. Please try again."
            );
          }
        })
        .catch((error) => {
          console.error("Logout error:", error);
          message.error("An error occurred during logout. Please try again.");
        });
    }
  };

  // User dropdown menu
  const userMenu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        Profile Settings
      </Menu.Item>
      <Menu.Item key="settings" icon={<SettingOutlined />}>
        Account Settings
      </Menu.Item>
      {/* Become Solution Partner â€” feature deferred, hidden for now */}
      {/* {companyType === 'DIRECT_CUSTOMER' && 
       partnerApplicationStatus !== 'pending' && 
       partnerApplicationStatus !== 'approved' && 
       partnerToastDismissed && (
        <Menu.Item 
          key="become-partner" 
          icon={<RocketOutlined style={{ color: '#fa8c16' }} />}
          style={{
            marginTop: '4px',
            marginBottom: '4px',
            fontWeight: 500,
          }}
        >
          <span style={{ 
            color: '#fa8c16',
            fontWeight: 500,
          }}>
            Become a Solution Partner âœ¨
          </span>
        </Menu.Item>
      )} */}
      {isEnterprisePlan && (
        <Menu.Item key="api-docs" icon={<ReadOutlined />}>
          API Documentation
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  // Helper function to convert hex color to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const currentKey = getCurrentMenuKey();
    const menuItem = menuItems.find((item) => item.key === currentKey);
    const hiddenTopBarKeys = new Set([
      "dashboard",
      "admin",
      "contacts",
      "templates",
      "broadcast",
      "inbox",
      "flow-responses",
      "analytics",
    ]);

    if (menuItem && hiddenTopBarKeys.has(menuItem.key)) {
      return "";
    }

    return menuItem
      ?
      menuItem.key === "dashboard"
        ? "Dashboard Overview"
        : menuItem.key === "admin"
          ? "MessageOS Admin Dashboard" :
          menuItem.key === "solution-partner-admin"
        ? "Solution Partner Admin Dashboard"
        : menuItem.key === "become-solution-partner"
        ? "Become a Solution Partner"
        : menuItem.label
      : "Dashboard";
  };

  const pageTitle = getPageTitle();

  // Filter out highlighted items for regular menu (they'll be shown as toast)
  const regularMenuItems = menuItems.filter(item => !item.highlighted);
  const partnerMenuItem = menuItems.find(item => item.key === 'become-solution-partner');
  const showPartnerToast = partnerMenuItem && !partnerToastDismissed;

  // Sidebar Content Component
  const SidebarContent = ({ isDrawer = false }) => {
    const isCollapsed = !isDrawer && collapsed;
    const emailSettingsSubNav = (
      <div className="mos-nav__subnav">
        <div className="mos-nav__subnav-title">
          Email Settings
        </div>
        {[
          { label: 'Sender Profile', path: '/dashboard/email/settings/sender-profile', icon: <UserOutlined style={{ fontSize: 14 }} />, pageKey: 'email_sender' },
          { label: 'Custom Domain', path: '/dashboard/email/settings/custom-domain', icon: <MailOutlined style={{ fontSize: 14 }} />, pageKey: 'email_sender' },
          { label: 'Preferences', path: '/dashboard/email/preferences', icon: <StopOutlined style={{ fontSize: 14 }} />, pageKey: 'email_settings' },
        ].filter(sub => !userCtx?.pages || hasPageAccess(userCtx, sub.pageKey)).map(sub => {
          const subActive = location.pathname === sub.path || location.pathname.startsWith(sub.path + '/');
          return (
            <div
              key={sub.path}
              className={`mos-nav__subnav-item${subActive ? ' mos-nav__subnav-item--active' : ''}`}
              onClick={() => {
                navigate(sub.path);
                if (isDrawer) setMobileDrawerVisible(false);
              }}
            >
              {sub.icon}
              <span>{sub.label}</span>
            </div>
          );
        })}
      </div>
    );
    const walletItemIndex = regularMenuItems.findIndex(item => item.key === 'wallet');

    const handleNavItemClick = (item) => {
      const planAlwaysAllowed = ['dashboard', 'wallet', 'admin'];
      if (!hasActivePlan && !planAlwaysAllowed.includes(item.key)) {
        navigate('/dashboard/pricing');
        if (isDrawer) setMobileDrawerVisible(false);
        return;
      }
      navigate(getItemPath(item));
      if (isDrawer) setMobileDrawerVisible(false);
    };
    const isItemLocked = (item) => {
      const planAlwaysAllowed = ['dashboard', 'wallet', 'admin'];
      if (!hasActivePlan && !planAlwaysAllowed.includes(item.key)) return true;
      const wabaOnlyItems = ['templates', 'broadcast', 'inbox', 'flow-responses', 'opted-out', 'analytics'];
      if (hasActivePlan && !wabaSetupComplete && channelMode === 'whatsapp' && wabaOnlyItems.includes(item.key)) return true;
      return false;
    };
    return (
      <div className={`mos-nav${isCollapsed ? ' mos-nav--collapsed' : ''}`}>
        {/* Drawer Header (mobile only) */}
        {isDrawer && (
          <div className="mos-nav__drawer-header">
            <img src={MessageOSLogo} alt="MessageOS" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
            <Button type="text" icon={<CloseOutlined style={{ fontSize: 16, color: '#6b7280' }} />} onClick={() => setMobileDrawerVisible(false)} style={{ padding: 4 }} />
          </div>
        )}
        {/* Sidebar Header â€” logo + toggle (desktop only) */}
        {!isDrawer && (
          <div className="mos-sidebar__header">
            <button className="mos-sidebar__toggle" onClick={() => setCollapsed(!collapsed)}>
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
            <img
              src={MessageOSLogo}
              alt="MessageOS"
              className="mos-sidebar__logo"
              onClick={() => navigate('/dashboard')}
            />
          </div>
        )}

        {/* Partner Toast (drawer only) */}
        {isDrawer && showPartnerToast && (
        <div
          style={{
            margin: "12px",
            padding: "14px 12px",
            background: "linear-gradient(135deg, #128C7E 0%, #25D366 50%, #20c65a 100%)",
            borderRadius: "12px",
            position: "relative",
            cursor: "pointer",
            boxShadow: "0 4px 15px rgba(37, 211, 102, 0.35), 0 2px 6px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.15)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
          onClick={() => {
            setMobileDrawerVisible(false);
            prefetchPartnerRequestAndNavigate();
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.45), 0 4px 10px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 211, 102, 0.35), 0 2px 6px rgba(0,0,0,0.1)';
          }}
        >
          {/* Close button */}
          <button
            onClick={dismissPartnerToast}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "rgba(255,255,255,0.25)",
              border: "none",
              borderRadius: "50%",
              width: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#fff",
              fontSize: "9px",
              padding: 0,
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
          >
            <CloseOutlined />
          </button>
          
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              background: "rgba(255,255,255,0.2)",
              borderRadius: "10px",
              padding: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}>
              <RocketOutlined style={{ fontSize: "20px", color: "#fff" }} />
            </div>
            <div style={{ flex: 1 }}>
              <Text style={{ 
                color: "#fff", 
                fontWeight: 700, 
                fontSize: "14px",
                display: "block",
                lineHeight: 1.3,
                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}>
                Become a Partner âœ¨
              </Text>
              <Text style={{ 
                color: "rgba(255,255,255,0.9)", 
                fontSize: "11px",
                display: "block",
                marginTop: "2px",
              }}>
                Unlock exclusive benefits
              </Text>
            </div>
          </div>
        </div>
      )}

        {/* Navigation Items */}
        <nav className="mos-nav__items">
          {regularMenuItems.map((item, index) => {
            const isActive = getCurrentMenuKey() === item.key;
            const locked = isItemLocked(item);
            const label = channelMode === 'email' && item.key === 'broadcast' ? 'Campaigns' : item.key === 'broadcast' ? 'Campaign' : item.label;
            return (
              <Fragment key={item.key}>
                <Tooltip title={isCollapsed ? label : null} placement="right">
                  <div
                    className={`mos-nav__item${isActive ? ' mos-nav__item--active' : ''}${locked ? ' mos-nav__item--locked' : ''}`}
                    onClick={() => handleNavItemClick(item)}
                  >
                    <span className="mos-nav__item-icon">
                      {item.iconComponent(isCollapsed)}
                      {item.key === 'inbox' && unreadMessageCount > 0 && (
                        <span className="mos-nav__badge">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>
                      )}
                    </span>
                    {!isCollapsed && (
                      <span className="mos-nav__item-text">
                        {label}
                        {userCtx && userCtx.pages && item.pageKey && !canWrite(userCtx, item.pageKey) && (
                          <span className="mos-nav__view-tag">(View)</span>
                        )}
                      </span>
                    )}
                  </div>
                </Tooltip>

                {channelMode === 'email' && !isCollapsed && walletItemIndex >= 0 && index === walletItemIndex && emailSettingsSubNav}
              </Fragment>
            );
          })}

          {channelMode === 'email' && !isCollapsed && walletItemIndex === -1 && emailSettingsSubNav}
        </nav>

      {/* Quick Stats in Sidebar */}
      {/* {(!collapsed || !screens.md) && (
        <div style={{ padding: '20px 16px', marginTop: '20px' }}>
          <Divider orientation="left" style={{ fontSize: '12px', color: '#666' }}>
            Quick Stats
          </Divider>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: '#f6ffed',
              borderRadius: '6px',
              border: '1px solid #b7eb8f'
            }}>
              <Text style={{ fontSize: '12px' }}>Active Contacts</Text>
              <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>
                {stats.activeContacts}
              </Text>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: '#f0f5ff',
              borderRadius: '6px',
              border: '1px solid #91d5ff'
            }}>
              <Text style={{ fontSize: '12px' }}>Messages Today</Text>
              <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>
                {Math.round(stats.messagesThisMonth / 30)}
              </Text>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: '#fff2e8',
              borderRadius: '6px',
              border: '1px solid #ffbb96'
            }}>
              <Text style={{ fontSize: '12px' }}>Today's Cost</Text>
              <Text strong style={{ fontSize: '12px', color: '#fa8c16' }}>
                ${stats.todayCost}
              </Text>
            </div>
          </Space>
        </div>
      )} */}
      </div>
    );
  };

  console.log(notificationCount);
  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Desktop persistent sidebar */}
      {screens.md && (
        <aside className={`mos-sidebar${collapsed ? ' mos-sidebar--collapsed' : ''}`}>
          <SidebarContent isDrawer={false} />
        </aside>
      )}

      {/* Mobile drawer */}
      <Drawer
        title={null}
        placement="left"
        onClose={() => setMobileDrawerVisible(false)}
        open={!screens.md && mobileDrawerVisible}
        className="mos-sidebar-drawer"
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: 'none' }}
        width={280}
        zIndex={1100}
      >
        <SidebarContent isDrawer={true} />
      </Drawer>

      <Layout style={{
        marginLeft: screens.md ? (collapsed ? 60 : 210) : 0,
        transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {/* Top Header */}
        <Header
          style={{
            background: "#fff",
            padding: 0,
            height: 56,
            lineHeight: '56px',
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            borderBottom: "1px solid #eef0f3",
            position: "sticky",
            top: 0,
            zIndex: 999,
          }}
        >
          <div className="mos-topbar">
          <div className="mos-topbar-left">
            {/* Mobile Burger Menu (hidden on desktop â€” toggle is in sidebar) */}
            {!screens.md && (
              <button
                onClick={() => setMobileDrawerVisible(true)}
                style={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: '1px solid #d0d7de',
                  borderRadius: 6,
                  cursor: 'pointer',
                  color: '#1f2328',
                  padding: 0,
                  flexShrink: 0,
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f6f8fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <MenuOutlined style={{ fontSize: 15 }} />
              </button>
            )}
            {/* Company Logo (mobile only â€” desktop logo is in sidebar) */}
            {!screens.md && (
              <img
                src={MessageOSLogo}
                alt={`${companyData.companyName || 'MessageOS'} Logo`}
                onClick={() => navigate('/dashboard')}
                className="mos-topbar-logo"
              />
            )}

            {/* Channel Mode Toggle */}
            <div
              className="mos-channel-toggle"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: '#f0f2f5',
                borderRadius: 10,
                padding: '3px',
                border: '1px solid #e4e8ee',
                userSelect: 'none',
                marginLeft: screens.sm ? 8 : 4,
                marginRight: screens.sm ? 0 : 10,
              }}
            >
              <div
                onClick={() => {
                  if (channelMode !== 'whatsapp') {
                    toggleChannelMode();
                    navigate('/dashboard');
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: screens.sm ? '6px 14px' : '5px 10px',
                  borderRadius: 8,
                  fontSize: screens.sm ? 13 : 12,
                  fontWeight: 600,
                  cursor: channelMode === 'whatsapp' ? 'default' : 'pointer',
                  background: channelMode === 'whatsapp' ? '#fff' : 'transparent',
                  color: channelMode === 'whatsapp' ? '#25D366' : '#999',
                  boxShadow: channelMode === 'whatsapp' ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                  transition: 'all .2s ease',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                <WhatsAppOutlined style={{ fontSize: screens.sm ? 15 : 14 }} />
                {screens.sm && 'WhatsApp'}
              </div>
              <div
                onClick={() => {
                  if (channelMode !== 'email') {
                    toggleChannelMode();
                    navigate('/dashboard/email');
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: screens.sm ? '6px 14px' : '5px 10px',
                  borderRadius: 8,
                  fontSize: screens.sm ? 13 : 12,
                  fontWeight: 600,
                  cursor: channelMode === 'email' ? 'default' : 'pointer',
                  background: channelMode === 'email' ? '#fff' : 'transparent',
                  color: channelMode === 'email' ? '#0036b0' : '#999',
                  boxShadow: channelMode === 'email' ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                  transition: 'all .2s ease',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                <MailOutlined style={{ fontSize: screens.sm ? 15 : 14 }} />
                {screens.sm && 'Email'}
              </div>
            </div>
          </div>

          <Space className="mos-topbar-actions" size={screens.md ? "middle" : "small"}>
            {/* WhatsApp Status - only shown in WhatsApp mode */}
            {/* Don't show "Not Connected" when there's an API error (500, 429, etc.) */}
            {channelMode === 'whatsapp' && (
            <div 
              style={{ 
                display: "flex", 
                alignItems: "center",
                cursor: (!wabaFetchError && !loadingWabaDetails && (!wabaDetails?.waba_id || !wabaDetails?.phone_number_id)) ? 'pointer' : 'default',
              }}
              onClick={() => {
                // Only navigate if we're sure user hasn't done signup (API succeeded but no data)
                if (!wabaFetchError && !loadingWabaDetails && (!wabaDetails?.waba_id || !wabaDetails?.phone_number_id)) {
                  navigate("/whatsappregistration");
                }
              }}
            >
              <WhatsAppOutlined
                style={{
                  fontSize: 16,
                  color: wabaFetchError ? '#8c8c8c' : (wabaDetails?.waba_id && wabaDetails?.phone_number_id) ? "#25D366" : "#ff4d4f",
                  marginRight: 6,
                }}
              />
              {screens.sm && (
                <Text 
                  style={{ 
                    fontSize: "13px", 
                    color: wabaFetchError ? '#8c8c8c' : (wabaDetails?.waba_id && wabaDetails?.phone_number_id) ? '#25D366' : '#ff4d4f', 
                    fontWeight: 500,
                    textDecoration: (!wabaFetchError && (!wabaDetails?.waba_id || !wabaDetails?.phone_number_id)) ? 'underline' : 'none'
                  }}
                >
                  {loadingWabaDetails ? 'Checking...' : wabaFetchError ? 'Checking...' : (wabaDetails?.waba_id && wabaDetails?.phone_number_id) ? 'Connected' : 'Not Connected'}
                </Text>
              )}
            </div>
            )}

            {/* Email Setup Guide Button */}
            {channelMode === 'email' && (
              <Button
                type="text"
                icon={<QuestionCircleOutlined style={{ fontSize: screens.xs ? 14 : 16 }} />}
                onClick={() => setEmailSetupModalVisible(true)}
                size={screens.md ? "middle" : "small"}
                title="Email Setup Guide"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#4f46e5',
                }}
              />
            )}

            {/* Product Guide */}
            <Button
              type="text"
              icon={<ReadOutlined style={{ fontSize: screens.xs ? 14 : 16 }} />}
              onClick={() => navigate('/dashboard/guide')}
              size={screens.md ? "middle" : "small"}
              title="Product Guide"
              style={{
                display: 'flex',
                alignItems: 'center',
                color: '#1f2328',
              }}
            />

            {/* Notifications */}
            <Dropdown
              overlay={
                <Menu 
                  style={{ 
                    width: screens.xs ? 280 : 360,
                    maxHeight: screens.xs ? 350 : 480,
                    overflow: "auto" 
                  }}
                >
                  <div
                    style={{
                      padding: screens.xs ? "8px 12px" : "12px 16px", // Less padding on mobile
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text strong style={{ fontSize: screens.xs ? 13 : 14 }}>
                      Notifications
                    </Text>
                    {notificationCount > 0 && (
                      <Text
                        type="secondary"
                        style={{ 
                          fontSize: screens.xs ? 10 : 12, 
                          cursor: "pointer" 
                        }}
                        onClick={() => hanldleMarkAllSeen()}
                      >
                        Mark all as read
                      </Text>
                    )}
                  </div>
                  {notificationLoading ? (
                    <div style={{ 
                      padding: screens.xs ? "16px 0" : "24px 0", 
                      textAlign: "center" 
                    }}>
                      <Spin size="small" />
                    </div>
                  ) : notifications.length > 0 ? (
                    notifications.slice(0, screens.xs ? 5 : 10).map((n, index) => ( // Limit items on mobile
                      <Menu.Item
                        key={n.notificationid || index}
                        onClick={() =>
                          handleNotificationClick([n.notificationid], n.status)
                        }
                        style={{
                          padding: screens.xs ? "8px 12px" : "12px 16px", // Less padding on mobile
                          margin: 0,
                          borderBottom: "1px solid #f5f5f5",
                          background: n.status === "seen" ? "transparent" : "#f0f7ff",
                          minHeight: screens.xs ? "auto" : "auto",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: screens.xs ? 8 : 12, // Smaller gap on mobile
                          }}
                        >
                          <div
                            style={{
                              width: screens.xs ? 6 : 8, // Smaller dot on mobile
                              height: screens.xs ? 6 : 8,
                              borderRadius: "50%",
                              background: n.status === "seen" ? "#d9d9d9" : "#1890ff",
                              marginTop: screens.xs ? 4 : 6,
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <Text
                              strong
                              style={{
                                fontSize: screens.xs ? 12 : 14, // Smaller text on mobile
                                display: "block",
                                marginBottom: 2,
                                lineHeight: screens.xs ? 1.3 : 1.4,
                              }}
                            >
                              {screens.xs && n.message.length > 40 
                                ? `${n.message.substring(0, 40)}...` 
                                : n.message}
                            </Text>
                            {/* {!screens.xs && (
                              <Text type="secondary" style={{ fontSize: 13 }}>
                                {n.message}
                              </Text>
                            )} */}
                            <Text
                              type="secondary"
                              style={{
                                fontSize: screens.xs ? 10 : 12,
                                display: "block",
                                marginTop: screens.xs ? 2 : 4,
                              }}
                            >
                              {n.updatetime 
                                ? moment(n.updatetime).fromNow() 
                                : (n.time || 'Just now')
                              }
                            </Text>
                          </div>
                        </div>
                      </Menu.Item>
                    ))
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No notifications"
                      style={{ 
                        margin: screens.xs ? "16px 0" : "32px 0",
                        fontSize: screens.xs ? 12 : 14 
                      }}
                    />
                  )}
                  {/* Show "View All" link on mobile if there are more notifications */}
                  {screens.xs && notifications.length > 5 && (
                    <div style={{ 
                      padding: "8px 12px", 
                      textAlign: "center", 
                      borderTop: "1px solid #f0f0f0" 
                    }}>
                      <Text 
                        type="primary" 
                        style={{ fontSize: 11, cursor: "pointer" }}
                        onClick={() => message.info("View all notifications coming soon")}
                      >
                        View All ({notifications.length})
                      </Text>
                    </div>
                  )}
                </Menu>
              }
              trigger={["click"]}
              placement={screens.xs ? "bottomLeft" : "bottomRight"}
              arrow={{ pointAtCenter: true }}
              overlayStyle={{ 
                zIndex: 9999 // High z-index to appear above table
              }}
              getPopupContainer={() => document.body} // Render in body instead of parent
            >
              <Badge count={notificationCount} size={screens.xs ? "small" : "small"}>
                <Button
                  type="text"
                  icon={<BellOutlined style={{ fontSize: screens.xs ? 14 : 16 }} />}
                  size={screens.md ? "middle" : "small"}
                />
              </Badge>
            </Dropdown>

            {/* User Menu */}
            <Dropdown overlay={userMenu} trigger={["click"]}>
              <Button
                type="text"
                style={{ display: "flex", alignItems: "center" }}
                size={screens.md ? "middle" : "small"}
              >
                <Avatar
                  src={companyLogoSrc || undefined}
                  icon={!companyLogoSrc ? <UserOutlined /> : undefined}
                  size={screens.md ? "default" : "small"}
                  style={companyLogoSrc ? {
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    objectFit: 'contain',
                    flexShrink: 0,
                  } : undefined}
                />
                {screens.sm && (
                  <Text
                    style={{
                      marginLeft: 8,
                      fontSize: screens.md ? "14px" : "12px",
                    }}
                  >
                    {profile?.companyname || currentUser?.name || 'My Company'}
                  </Text>
                )}
              </Button>
            </Dropdown>
          </Space>
          </div>
        </Header>

        {/* Main Content */}
        <Content
          style={{
            minHeight: "calc(100vh - 56px)",
            padding: '14px',
            width: '100%',
          }}
        >
          {/* Onboarding banner â€” no active plan */}
          {onboardingStatus && !hasActivePlan && isOnPlanAllowedPage && (
            <div
              onClick={() => navigate('/dashboard/pricing')}
              style={{
                marginBottom: 16,
                padding: '14px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(102,126,234,0.35)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(102,126,234,0.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(102,126,234,0.35)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <RocketOutlined style={{ fontSize: 18, color: '#fff' }} />
                </div>
                <div>
                  <Text strong style={{ color: '#fff', fontSize: 14, display: 'block', lineHeight: 1.3 }}>
                    Get started — choose a plan
                  </Text>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                    Unlock email &amp; WhatsApp campaigns, contacts, analytics and more
                  </Text>
                </div>
              </div>
              <Button
                size="small"
                style={{
                  background: '#fff',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 16px',
                  height: 32,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  flexShrink: 0,
                }}
                onClick={e => { e.stopPropagation(); navigate('/dashboard/pricing'); }}
              >
              View Plans →
              </Button>
            </div>
          )}
          {onboardingStatus && hasActivePlan && !wabaSetupComplete && channelMode === 'whatsapp' && !isOnWabaGatedPage && !isOnEmailPage && (
            <Alert
              type="info"
              showIcon
              banner
              message="WhatsApp not connected"
              description="Complete WhatsApp Business embedded signup to access WhatsApp messaging, templates, and campaigns."
              action={
                <Button size="small" onClick={() => navigate('/whatsappregistration')}>
                  Set up now
                </Button>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Route-level content blocking: blur content + overlay when gated */}
          {(() => {
            const isPlanBlocked = onboardingStatus && !hasActivePlan && !isOnPlanAllowedPage;
            // WABA blocked: ONLY in WhatsApp mode, on a WA-specific page, with no WABA setup
            // Email mode is NEVER WABA-blocked â€” fully accessible after plan purchase
            const isWabaBlocked = onboardingStatus && hasActivePlan && !wabaSetupComplete
              && channelMode === 'whatsapp' && isOnWabaGatedPage && !isOnEmailPage && !isOnContactsPage;
            const isBlocked = isPlanBlocked || isWabaBlocked;
            return (
              <div style={{ position: 'relative' }}>
                {isBlocked && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 10,
                      background: 'rgba(255,255,255,0.7)',
                      backdropFilter: 'blur(4px)',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 300,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      // Navigate directly â€” no modal popup
                      if (isPlanBlocked) navigate('/dashboard/pricing');
                      else if (isWabaBlocked) navigate('/whatsappregistration');
                    }}
                  >
                    <div style={{ textAlign: 'center', padding: 24 }}>
                      <div style={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: isPlanBlocked
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 16px',
                        boxShadow: isPlanBlocked
                          ? '0 4px 20px rgba(102,126,234,0.3)'
                          : '0 4px 20px rgba(37,211,102,0.3)',
                      }}>
                        {isPlanBlocked
                          ? <RocketOutlined style={{ fontSize: 28, color: '#fff' }} />
                          : <WhatsAppOutlined style={{ fontSize: 28, color: '#fff' }} />
                        }
                      </div>
                      <Text strong style={{ fontSize: 16, color: '#262626', display: 'block', marginBottom: 4 }}>
                        {isPlanBlocked ? 'Plan Required' : 'WhatsApp Setup Required'}
                      </Text>
                      <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                        {isPlanBlocked
                          ? 'Click here or choose a plan to unlock this feature'
                          : 'Click here to complete WhatsApp Business embedded signup'}
                      </Text>
                    </div>
                  </div>
                )}
                <div style={isBlocked ? { filter: 'blur(3px)', pointerEvents: 'none', userSelect: 'none' } : undefined}>
                  <Outlet />
                </div>
              </div>
            );
          })()}
        </Content>
      </Layout>

      {/* Tutorial Modal with StepsContainer */}
      <Modal
        title={null}
        open={tutorialModalVisible}
        onCancel={() => setTutorialModalVisible(false)}
        footer={null}
        className="dashboard-tutorial-modal"
        width={screens.xs ? '95%' : screens.md ? 1000 : 1300}
        centered
        bodyStyle={{ 
          padding: 0, 
          height: screens.xs ? 'auto' : 'min(600px, calc(100vh - 64px))', 
          maxHeight: screens.xs ? '90vh' : 'calc(100vh - 64px)',
          overflow: 'hidden' 
        }}
        destroyOnClose
      >
        <StepsContainer
          steps={[
            'Create Account',
            'Connect WhatsApp',
            'Add Contacts',
            'Pricing',
            'Send Messages'
          ]}
          renderStep={(currentStep, helpers) => {
            switch (currentStep) {
              case 0:
                return (
                  <div style={{ padding: screens.xs ? '12px' : '20px' }}>
                    <h2 style={{ color: '#1890ff', marginBottom: screens.xs ? '10px' : '16px', fontSize: screens.xs ? '16px' : '20px' }}>
                      Welcome to MessageOS
                    </h2>
                    <p style={{ fontSize: screens.xs ? '12px' : '15px', color: '#595959', lineHeight: '1.5' }}>
                      Get started with our platform in just a few simple steps. 
                      This guide will walk you through setting up your account and 
                      connecting your WhatsApp Business account.
                    </p>
                    <ul style={{ marginTop: screens.xs ? '12px' : '20px', fontSize: screens.xs ? '12px' : '14px', color: '#262626' }}>
                      <li style={{ marginBottom: screens.xs ? '8px' : '12px' }}>
                        âœ“ Create your company profile
                      </li>
                      <li style={{ marginBottom: screens.xs ? '8px' : '12px' }}>
                        âœ“ Verify your email and phone number
                      </li>
                      <li style={{ marginBottom: screens.xs ? '8px' : '12px' }}>
                        âœ“ Set up your business information
                      </li>
                    </ul>
                  </div>
                );
              case 1:
                return (
                  <div style={{ padding: screens.xs ? '12px' : '20px' }}>
                    <h2 style={{ color: '#1890ff', marginBottom: screens.xs ? '10px' : '16px', fontSize: screens.xs ? '16px' : '20px' }}>
                      Requirements for WhatsApp Business Setup
                    </h2>
                    <p style={{ fontSize: screens.xs ? '12px' : '14px', color: '#595959', lineHeight: '1.5', marginBottom: screens.xs ? '12px' : '20px' }}>
                      Before connecting your WhatsApp Business account, please ensure you have the following:
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: screens.xs ? '1fr' : '1fr 1fr', gap: screens.xs ? '10px' : '16px' }}>
                      {/* Facebook Page/Account */}
                      <div style={{ 
                        border: '2px solid #1890ff', 
                        borderRadius: screens.xs ? '8px' : '12px', 
                        padding: screens.xs ? '12px' : '20px',
                        background: '#f0f7ff'
                      }}>
                        <div style={{ fontSize: screens.xs ? '24px' : '32px', marginBottom: screens.xs ? '6px' : '12px' }}>ðŸ“˜</div>
                        <h4 style={{ color: '#0958d9', margin: '0 0 6px 0', fontSize: screens.xs ? '13px' : '16px' }}>
                          Facebook Page/Account
                        </h4>
                        <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>
                          A Facebook Business account or page is required to connect WhatsApp Business API
                        </p>
                      </div>

                      {/* New Mobile Number */}
                      <div style={{ 
                        border: '2px solid #52c41a', 
                        borderRadius: screens.xs ? '8px' : '12px', 
                        padding: screens.xs ? '12px' : '20px',
                        background: '#f6ffed'
                      }}>
                        <div style={{ fontSize: screens.xs ? '24px' : '32px', marginBottom: screens.xs ? '6px' : '12px' }}>ðŸ“±</div>
                        <h4 style={{ color: '#389e0d', margin: '0 0 6px 0', fontSize: screens.xs ? '13px' : '16px' }}>
                          New Mobile Number
                        </h4>
                        <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>
                          A dedicated phone number that is not registered on WhatsApp or WhatsApp Business app
                        </p>
                      </div>

                      {/* HTTPS Website */}
                      <div style={{ 
                        border: '2px solid #722ed1', 
                        borderRadius: screens.xs ? '8px' : '12px', 
                        padding: screens.xs ? '12px' : '20px',
                        background: '#f9f0ff'
                      }}>
                        <div style={{ fontSize: screens.xs ? '24px' : '32px', marginBottom: screens.xs ? '6px' : '12px' }}>ðŸ”’</div>
                        <h4 style={{ color: '#531dab', margin: '0 0 6px 0', fontSize: screens.xs ? '13px' : '16px' }}>
                          HTTPS Website
                        </h4>
                        <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>
                          Your business website must have a valid SSL certificate (https://)
                        </p>
                      </div>

                      {/* Payment Card */}
                      <div style={{ 
                        border: '2px solid #fa8c16', 
                        borderRadius: screens.xs ? '8px' : '12px', 
                        padding: screens.xs ? '12px' : '20px',
                        background: '#fff7e6'
                      }}>
                        <div style={{ fontSize: screens.xs ? '24px' : '32px', marginBottom: screens.xs ? '6px' : '12px' }}>ðŸ’³</div>
                        <h4 style={{ color: '#d46b08', margin: '0 0 6px 0', fontSize: screens.xs ? '13px' : '16px' }}>
                          Payment Card
                        </h4>
                        <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>
                          A valid credit/debit card for Facebook Business Manager verification
                        </p>
                      </div>
                    </div>

                    <div style={{ 
                      marginTop: screens.xs ? '12px' : '20px', 
                      padding: screens.xs ? '10px' : '16px', 
                      background: '#fffbe6',
                      border: '1px solid #ffe58f',
                      borderRadius: screens.xs ? '6px' : '8px'
                    }}>
                      <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>
                        <strong style={{ color: '#d48806' }}>ðŸ’¡ Tip:</strong> Once you have all requirements ready, 
                        click on the "Not Connected" status in the header to start the setup process.
                      </p>
                    </div>
                  </div>
                );
              case 2:
                return (
                  <div style={{ padding: screens.xs ? '12px' : '20px' }}>
                    <h2 style={{ color: '#1890ff', marginBottom: screens.xs ? '10px' : '16px', fontSize: screens.xs ? '16px' : '20px' }}>
                      Add Your Contacts
                    </h2>
                    <p style={{ fontSize: screens.xs ? '12px' : '15px', color: '#595959', lineHeight: '1.5' }}>
                      Import your customer contacts to start messaging them. You can add 
                      contacts individually or upload them in bulk using a CSV file.
                    </p>
                    <div style={{ marginTop: screens.xs ? '12px' : '20px' }}>
                      <h4 style={{ color: '#262626', marginBottom: screens.xs ? '8px' : '12px', fontSize: screens.xs ? '14px' : '16px' }}>
                        Import Methods:
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: screens.xs ? '1fr' : '1fr 1fr', gap: screens.xs ? '10px' : '16px' }}>
                        <div style={{ 
                          border: '1px solid #e8e8e8', 
                          padding: screens.xs ? '12px' : '16px', 
                          borderRadius: screens.xs ? '6px' : '8px' 
                        }}>
                          <h5 style={{ color: '#1890ff', fontSize: screens.xs ? '13px' : '15px' }}>ðŸ“ Manual Entry</h5>
                          <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: '4px 0 0' }}>
                            Add contacts one by one with their details
                          </p>
                        </div>
                        <div style={{ 
                          border: '1px solid #e8e8e8', 
                          padding: screens.xs ? '12px' : '16px', 
                          borderRadius: screens.xs ? '6px' : '8px' 
                        }}>
                          <h5 style={{ color: '#52c41a', fontSize: screens.xs ? '13px' : '15px' }}>ðŸ“ CSV Upload</h5>
                          <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: '4px 0 0' }}>
                            Import multiple contacts at once
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              case 3:
                return (
                  <div style={{ padding: screens.xs ? '12px' : '20px' }}>
                    <div style={{ marginBottom: '1.75rem' }}>
                      <h2 style={{ fontSize: screens.xs ? '16px' : '18px', fontWeight: 500, color: '#1a1a1a', marginBottom: '.25rem' }}>Simple, transparent pricing</h2>
                      <p style={{ fontSize: screens.xs ? '12px' : '13px', color: '#555' }}>Know exactly what you pay — platform fees + Meta charges.</p>
                    </div>

                    {/* Platform Fees */}
                    <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: '#888', marginBottom: '.75rem' }}>Platform fees (what we charge)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: screens.xs ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: '10px', marginBottom: '1.5rem' }}>
                      <div style={{ background: '#fff', border: '.5px solid #e5e5e5', borderLeft: '3px solid #D4537E', borderRadius: '0 12px 12px 0', padding: '1rem 1.1rem' }}>
                        <div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>Marketing</div>
                        <div style={{ fontSize: screens.xs ? '18px' : '22px', fontWeight: 500, color: '#D4537E', margin: '.2rem 0' }}>â‚¹ 0.02 / msg</div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>per message sent</div>
                      </div>
                      <div style={{ background: '#fff', border: '.5px solid #e5e5e5', borderLeft: '3px solid #378ADD', borderRadius: '0 12px 12px 0', padding: '1rem 1.1rem' }}>
                        <div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>Utility</div>
                        <div style={{ fontSize: screens.xs ? '18px' : '22px', fontWeight: 500, color: '#378ADD', margin: '.2rem 0' }}>â‚¹ 1.00 / msg</div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>per message sent</div>
                      </div>
                      <div style={{ background: '#fff', border: '.5px solid #e5e5e5', borderLeft: '3px solid #639922', borderRadius: '0 12px 12px 0', padding: '1rem 1.1rem' }}>
                        <div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>Authentication</div>
                        <div style={{ fontSize: screens.xs ? '18px' : '22px', fontWeight: 500, color: '#639922', margin: '.2rem 0' }}>â‚¹ 0.30 / msg</div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>per message sent</div>
                      </div>
                    </div>

                    {/* Meta Charges */}
                    <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: '#888', marginBottom: '.75rem' }}>Meta charges (India)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: screens.xs ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: '10px', marginBottom: '1.5rem' }}>
                      <div style={{ background: '#f9f9f9', border: '.5px solid #e5e5e5', borderRadius: '12px', padding: screens.xs ? '.8rem .9rem' : '1rem 1.1rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#999' }}>Marketing</span>
                        <span style={{ fontSize: screens.xs ? '12px' : '13px', fontWeight: 500, color: '#555', lineHeight: 1.4 }}>â‚¹ 0.7846 / msg</span>
                      </div>
                      <div style={{ background: '#f9f9f9', border: '.5px solid #e5e5e5', borderRadius: '12px', padding: screens.xs ? '.8rem .9rem' : '1rem 1.1rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#999' }}>Utility</span>
                        <span style={{ fontSize: screens.xs ? '12px' : '13px', fontWeight: 500, color: '#555', lineHeight: 1.4 }}>â‚¹ 0.1150 / msg</span>
                      </div>
                      <div style={{ background: '#f9f9f9', border: '.5px solid #e5e5e5', borderRadius: '12px', padding: screens.xs ? '.8rem .9rem' : '1rem 1.1rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#999' }}>Authentication</span>
                        <span style={{ fontSize: screens.xs ? '12px' : '13px', fontWeight: 500, color: '#555', lineHeight: 1.4 }}>â‚¹ 0.1150 / msg</span>
                      </div>
                    </div>

                    <hr style={{ border: 'none', borderTop: '.5px solid #e5e5e5', margin: '1.5rem 0' }} />

                    {/* Subscription Plans */}
                    <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: '#888', marginBottom: '.75rem' }}>Subscription plans</div>
                    <div style={{ display: 'grid', gridTemplateColumns: screens.xs ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '10px', marginBottom: '1.5rem' }}>
                      <div style={{ background: '#fff', border: '.5px solid #e5e5e5', borderRadius: '12px', padding: '1rem 1.1rem' }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '.3rem' }}>ðŸ’Ž Premium</div>
                        <div style={{ fontSize: screens.xs ? '18px' : '20px', fontWeight: 500, color: '#1a1a1a' }}>â‚¹ 1,999 <span style={{ fontSize: '12px', fontWeight: 400, color: '#999' }}>/ month</span></div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '.35rem', lineHeight: 1.4 }}>Great for growing businesses</div>
                        <div style={{ fontSize: '11px', color: '#555', marginTop: '.5rem', paddingTop: '.5rem', borderTop: '.5px solid #e5e5e5' }}>ðŸ“§ 25,000 emails/cycle Â· ðŸ’¬ 300 WA campaigns</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ alignSelf: 'center', display: 'inline-block', fontSize: '10px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px', marginBottom: '.4rem', background: '#e6f1fb', color: '#185FA5' }}>Most popular</span>
                        <div style={{ background: '#fff', border: '2px solid #378ADD', borderRadius: '12px', padding: '1rem 1.1rem', flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '.3rem' }}>ðŸš€ Enterprise</div>
                          <div style={{ fontSize: screens.xs ? '18px' : '20px', fontWeight: 500, color: '#1a1a1a' }}>â‚¹ 4,999 <span style={{ fontSize: '12px', fontWeight: 400, color: '#999' }}>/ month</span></div>
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '.35rem', lineHeight: 1.4 }}>Large-scale with unlimited WhatsApp</div>
                          <div style={{ fontSize: '11px', color: '#555', marginTop: '.5rem', paddingTop: '.5rem', borderTop: '.5px solid #e5e5e5' }}>ðŸ“§ 50,000 emails/cycle Â· ðŸ’¬ Unlimited WA campaigns</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ fontSize: '11px', color: '#999', marginTop: '1.5rem', textAlign: 'center' }}>Plan quotas reset each billing cycle Â· Annual plans save ~17%</div>
                  </div>
                );
              case 4:
                return (
                  <div style={{ padding: screens.xs ? '12px' : '20px' }}>
                    <h2 style={{ color: '#1890ff', marginBottom: screens.xs ? '10px' : '16px', fontSize: screens.xs ? '16px' : '20px' }}>
                      Start Sending Messages
                    </h2>
                    <p style={{ fontSize: screens.xs ? '12px' : '15px', color: '#595959', lineHeight: '1.5' }}>
                      You're all set! Now you can start sending messages to your customers. 
                      Create templates, broadcast messages, and manage conversations.
                    </p>
                    <div style={{ marginTop: screens.xs ? '12px' : '24px' }}>
                      <h4 style={{ color: '#262626', marginBottom: screens.xs ? '10px' : '16px', fontSize: screens.xs ? '14px' : '16px' }}>
                        Key Features:
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: screens.xs ? '10px' : '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'start', gap: screens.xs ? '8px' : '12px' }}>
                          <span style={{ fontSize: screens.xs ? '16px' : '20px' }}>ðŸ“‹</span>
                          <div>
                            <h5 style={{ margin: 0, color: '#262626', fontSize: screens.xs ? '13px' : '15px' }}>Templates</h5>
                            <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: '2px 0 0' }}>
                              Create and manage message templates
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'start', gap: screens.xs ? '8px' : '12px' }}>
                          <span style={{ fontSize: screens.xs ? '16px' : '20px' }}>ðŸ“¢</span>
                          <div>
                            <h5 style={{ margin: 0, color: '#262626', fontSize: screens.xs ? '13px' : '15px' }}>Campaigns</h5>
                            <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: '2px 0 0' }}>
                              Send messages to multiple contacts
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'start', gap: screens.xs ? '8px' : '12px' }}>
                          <span style={{ fontSize: screens.xs ? '16px' : '20px' }}>ðŸ’¬</span>
                          <div>
                            <h5 style={{ margin: 0, color: '#262626', fontSize: screens.xs ? '13px' : '15px' }}>Inbox</h5>
                            <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: '2px 0 0' }}>
                              Manage conversations with customers
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              default:
                return null;
            }
          }}
          onStepChange={(step, direction) => {
            console.log(`Tutorial step ${step} (${direction})`);
          }}
          imageUrl={[companyData.companyLogo || "/vertical-transparent-light.png", ConnectStepImage, ContactsStepImage, PricingStepImage, SendStepImage]}
          imagePosition="left"
          onFinish={() => {
            // message.success('Tutorial completed! Start using MessageOS now.');
            setTutorialModalVisible(false);
            navigate('/whatsappregistration');
            
          }}
          finishButtonText="Get Started"
        />
      </Modal>

      {/* Email Setup Guide Modal */}
      <Modal
        title="Email Setup Guide"
        open={emailSetupModalVisible}
        onCancel={() => setEmailSetupModalVisible(false)}
        footer={null}
        width={screens.xs ? '95%' : 640}
        centered
        destroyOnClose
      >
        <div style={{ padding: screens.xs ? '8px 0' : '12px 0' }}>
          <p style={{ fontSize: screens.xs ? '12px' : '14px', color: '#595959', marginBottom: screens.xs ? '16px' : '24px', lineHeight: '1.6' }}>
            Follow these steps to set up and start sending email campaigns:
          </p>

          {/* Step 1 â€” Sender Profile */}
          <div style={{ display: 'flex', gap: screens.xs ? '10px' : '14px', marginBottom: screens.xs ? '16px' : '20px' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>1</div>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: screens.xs ? '13px' : '15px', color: '#1a1a2e' }}>Set Up Sender Profile</h4>
              <p style={{ margin: 0, fontSize: screens.xs ? '11px' : '13px', color: '#595959', lineHeight: '1.5' }}>
                Go to <a style={{ color: '#4f46e5', cursor: 'pointer' }} onClick={() => { setEmailSetupModalVisible(false); navigate('/dashboard/email/settings/sender-profile'); }}>Sender Profile</a> and configure your sender name, email handle, and reply-to address. This controls how your emails appear to recipients.
              </p>
            </div>
          </div>

          {/* Step 2 â€” Custom Domain (Optional) */}
          <div style={{ display: 'flex', gap: screens.xs ? '10px' : '14px', marginBottom: screens.xs ? '16px' : '20px' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>2</div>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: screens.xs ? '13px' : '15px', color: '#1a1a2e' }}>Custom Domain (Optional, Enterprise)</h4>
              <p style={{ margin: 0, fontSize: screens.xs ? '11px' : '13px', color: '#595959', lineHeight: '1.5' }}>
                For full branding, go to <a style={{ color: '#4f46e5', cursor: 'pointer' }} onClick={() => { setEmailSetupModalVisible(false); navigate('/dashboard/email/settings/custom-domain'); }}>Custom Domain</a> to send from your own domain. Otherwise emails go via the shared platform domain (mail.messageos.io).
              </p>
            </div>
          </div>

          {/* Step 3 â€” Template */}
          <div style={{ display: 'flex', gap: screens.xs ? '10px' : '14px', marginBottom: screens.xs ? '16px' : '20px' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>3</div>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: screens.xs ? '13px' : '15px', color: '#1a1a2e' }}>Create Email Template</h4>
              <p style={{ margin: 0, fontSize: screens.xs ? '11px' : '13px', color: '#595959', lineHeight: '1.5' }}>
                Go to <a style={{ color: '#4f46e5', cursor: 'pointer' }} onClick={() => { setEmailSetupModalVisible(false); navigate('/dashboard/email/templates/new'); }}>Templates</a> and design a professional email using our visual editor. Add your logo, content, social links, and personalisation variables.
              </p>
            </div>
          </div>

          {/* Step 4 â€” Campaign */}
          <div style={{ display: 'flex', gap: screens.xs ? '10px' : '14px', marginBottom: screens.xs ? '16px' : '20px' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>4</div>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: screens.xs ? '13px' : '15px', color: '#1a1a2e' }}>Send Your Campaign</h4>
              <p style={{ margin: 0, fontSize: screens.xs ? '11px' : '13px', color: '#595959', lineHeight: '1.5' }}>
                Go to <a style={{ color: '#4f46e5', cursor: 'pointer' }} onClick={() => { setEmailSetupModalVisible(false); navigate('/dashboard/email/campaigns/new'); }}>Campaigns</a>, select a template, pick your audience, choose a sending mode, and launch your campaign!
              </p>
            </div>
          </div>

          {/* Sending approaches tip */}
          <div style={{ padding: screens.xs ? '10px' : '14px', background: '#f0f0ff', border: '1px solid #d9d6fe', borderRadius: 8, marginTop: screens.xs ? '12px' : '16px' }}>
            <p style={{ fontSize: screens.xs ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.5' }}>
              <strong style={{ color: '#4f46e5' }}>Two Sending Approaches:</strong><br />
              <strong>Approach 1 (Shared Domain)</strong> — Send from <code>name@mail.messageos.io</code>. Works immediately, no DNS changes needed. Available on all plans.<br />
              <strong>Approach 2 (Custom Domain)</strong> — Send from <code>campaigns@yourdomain.com</code>. Requires DNS setup. Enterprise plan only. Better deliverability and brand trust.
            </p>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
